import { Stack, TablePagination, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AccessEvent, MobileOpCode } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { getTranslatedReason } from 'utils/AccessEvent/AccessEvent';
import AccessEventsDataGrid, { AccessEventRow } from 'v2/components/AccessEventsDataGrid/AccessEventsDataGrid';
import { DataGridCard, DataGridCards } from 'v2/components/DataGridCards/DataGridCards';
import LockIconNameCell from 'v2/components/LockIconNameCell/LockIconNameCell';
import StatusChip from 'v2/components/StatusChip/StatusChip';
import { EMPTY_VALUE_PLACEHOLDER } from 'v2/constants';
import { PAGE_SIZE } from 'v2/hooks/useAccessEvents/useAccessEvents';
import { formatDate } from 'v2/utils/dateTime';

interface Props {
  rows: AccessEvent[];
  loading: boolean;
  rowCount: number;
  page: number;
  onPageChange: (page: number) => void;
}

const TableWithData: React.FC<Props> = ({ rows, loading, rowCount, page, onPageChange }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const normalizedRows: AccessEventRow[] = rows.map((event) => ({
    id: event.id,
    createdAt: event.createdAt,
    action: event.action,
    reason: event.reason,
    visitorName: event.visitor.name,
    visitorEmail: event.visitor.email,
    doorName: event.door.name,
    doorType: event.door.type ?? undefined
  }));

  if (isMobile) {
    return (
      <>
        <DataGridCards loading={loading}>
          {rows.map((row, index) => {
            const granted = row.action === MobileOpCode.AccessGranted;
            const reason = getTranslatedReason(row.reason ?? EMPTY_VALUE_PLACEHOLDER, translation);
            return (
              <DataGridCard key={row.id} id={`row-${index}`}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={2} justifyContent="space-between">
                    <Stack flexGrow={1}>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {translation.access}
                      </Typography>
                      <StatusChip
                        variant={granted ? 'complete' : 'incomplete'}
                        label={granted ? translation.granted : translation.denied}
                      />
                    </Stack>
                    <Stack alignItems="flex-end">
                      <Typography variant="caption" color="textSecondary" display="block">
                        {formatDate(row.createdAt, 'dateOnlyLong')}
                      </Typography>
                      <Typography variant="body2">{formatDate(row.createdAt, 'timeOnlyLong')}</Typography>
                    </Stack>
                  </Stack>
                  <Stack>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {translation.device}
                    </Typography>
                    <LockIconNameCell door={{ name: row.door.name }} />
                  </Stack>
                  <Stack overflow="hidden" minWidth={0}>
                    <Typography variant="body2" noWrap>
                      {row.visitor.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" noWrap>
                      {row.visitor.email}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="caption" color="textSecondary">
                      {translation.details}
                    </Typography>
                    <Typography variant="body2">{reason || EMPTY_VALUE_PLACEHOLDER}</Typography>
                  </Stack>
                </Stack>
              </DataGridCard>
            );
          })}
        </DataGridCards>
        <TablePagination
          component="div"
          count={rowCount}
          page={page}
          onPageChange={(_, newPage) => onPageChange(newPage)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          labelDisplayedRows={({ from, to }) => `${from}–${to}`}
          slotProps={{
            actions: {
              previousButton: { id: 'previous-page-button' },
              nextButton: { id: 'next-page-button' }
            }
          }}
        />
      </>
    );
  }

  return (
    <AccessEventsDataGrid
      className="not-clickable"
      rows={normalizedRows}
      loading={loading}
      rowCount={rowCount}
      page={page}
      onPageChange={onPageChange}
    />
  );
};

export default TableWithData;
